import { useCallback, useMemo, useState } from 'react'
import { ChainId, Currency, CurrencyAmount, Token } from '@pancakeswap/sdk'
import { useTranslation } from '@pancakeswap/localization'
import { ModalContainer, ModalHeader, ModalTitle, ModalCloseButton, ModalBody, Heading, Modal, Box, Slider, Button, Flex, Text, FlexGap, Balance, IconButton, PencilIcon, TooltipText, useTooltip, useModal, Dots } from '@pancakeswap/uikit'
import { useCurrency } from 'hooks/Tokens'
import RemoveStableLiquidity from 'views/RemoveLiquidity/RemoveStableLiquidity'
import RemoveLiquidityV2FormProvider from 'views/RemoveLiquidity/RemoveLiquidityV2FormProvider'
import useStableConfig, { StableConfigContext, useStableSwapTripleContract } from 'views/Swap/hooks/useStableConfig'
import { pDAI, pUSDC, pUSDT } from '@pancakeswap/tokens'
import styled from 'styled-components'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import { useUserSlippage } from '@pancakeswap/utils/user'
import { CurrencyLogo } from 'components/Logo'
import ConnectWalletButton from 'components/ConnectWalletButton'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useCustomToast, useTransactionAdder } from 'state/transactions/hooks'
import TransactionConfirmationModal from 'components/TransactionConfirmationModal'
import SettingsModal from 'components/EMenu/GlobalSettings/SettingsModal'
import { SettingsMode } from 'components/EMenu/GlobalSettings/types'
import { formatAmount } from 'utils/formatCurrencyAmount'
import useTokenBalance from 'hooks/useTokenBalance'
import useTotalSupply from 'hooks/useTotalSupply'
import NineInchConfig from '@9inch/config'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import useCatchTxError from 'hooks/useCatchTxError'
import { useFeeDataWithGasPrice } from 'state/user/hooks'
import { formatUnits, parseUnits } from 'viem'
import { useSingleContractMultipleData } from 'state/multicall/hooks'
import { PoolData } from '../hooks/usePools'

const SliderContainer = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  background: #2B2634;
  border-radius: 16px;
  padding: 16px 20px;
  
  @media screen and (max-width: 480px) {
    gap: 4px;
  }
`

const SliderRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  
  @media screen and (max-width: 480px) {
    gap: 8px;
  }
`

const ButtonGrid = styled(Flex)`
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 4px;
  background: #231F2B;
  padding: 4px;
  border-radius: 12px;
  
  @media screen and (max-width: 480px) {
    gap: 4px;
  }
`

const PercentageButton = styled(Button).attrs({ scale: "sm" }) <{ selected: boolean }>`
  font-size: 15px;
  flex: 1;
  padding: 8px;
  height: 38px;
  border-radius: 8px;
  background: ${({ selected }) => selected ? '#49415A' : 'transparent'};
  box-shadow: none;
`

const Hr = styled.hr`
  width: 100%;
  border-color: #FFFFFF17;
  border-width: 1px 0 0 0;
  margin: 1em 0;
`

interface DecreaseModalProps {
  pool: PoolData
  onDismiss?: () => void
}

const DecreaseModalStable: React.FC<DecreaseModalProps> = ({
  pool, onDismiss
}) => {
  const { t } = useTranslation()

  const { account, chainId } = useActiveWeb3React()

  const currency0 = useCurrency(pool.token0.address)
  const currency1 = useCurrency(pool.token1.address)
  const currency2 = useCurrency(pool.token2.address)

  const lpToken = new Token(ChainId.PULSE, pool.address, 18, 'pUSD')

  const [approvingTxn, setApprovingTxn] = useState(false)
  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [percent, setPercent] = useState(0)

  const stableSwapContract = useStableSwapTripleContract(NineInchConfig[chainId].SwapContract_USDC_USDT_DAI as `0x${string}`)

  const { balance } = useTokenBalance(pool.address)
  const userPoolBalance = CurrencyAmount.fromRawAmount(lpToken, balance?.toString() ?? 0n)
  const totalPoolTokens = useTotalSupply(lpToken)

  const share = userPoolBalance && totalPoolTokens ? Number(userPoolBalance.quotient) / Number(totalPoolTokens.quotient) : 0

  const [balance0, balance1, balance2] = useSingleContractMultipleData({
    contract: stableSwapContract,
    functionName: 'balances',
    args: [[0n], [1n], [2n]],
  })

  const token0Deposited = Number(balance0?.valid && balance0?.result ? formatUnits(balance0.result, pool.token0.decimals) : pool.reserve0) * share
  const token1Deposited = Number(balance1?.valid && balance1?.result ? formatUnits(balance1.result, pool.token1.decimals) : pool.reserve1) * share
  const token2Deposited = Number(balance2?.valid && balance2?.result ? formatUnits(balance2.result, pool.token2.decimals) : pool.reserve2) * share

  const deadline = useTransactionDeadline()
  const [allowedSlippage] = useUserSlippage()

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(`Based on last 7 days' performance. Does not account for impermanent loss`),
    {
      placement: 'bottom',
    },
  )

  const { toastRejection } = useCustomToast()

  const [onPresentSettingsModal] = useModal(<SettingsModal mode={SettingsMode.SWAP_LIQUIDITY} />)

  const addTransaction = useTransactionAdder()

  const amountToRemove = userPoolBalance.multiply(percent).divide(100)

  const { approvalState, approveCallback } = useApproveCallback(
    amountToRemove,
    stableSwapContract?.address,
  )

  const { fetchTxResponse } = useCatchTxError()
  const { gasPrice } = useFeeDataWithGasPrice()

  const typedAmount0 = Number(token0Deposited * percent / 100 || '0')
  const typedAmount1 = Number(token1Deposited * percent / 100 || '0')
  const typedAmount2 = Number(token2Deposited * percent / 100 || '0')

  const onRemove = useCallback(async () => {
    if (!percent || !stableSwapContract)
      return
    try {
      setAttemptingTxn(true)
      const currencies = [currency0, currency1, currency2]
      
      const tokenAmounts = [
        parseUnits(String(typedAmount0 * (10000 - allowedSlippage) / 10000), currency0.decimals),
        parseUnits(String(typedAmount1 * (10000 - allowedSlippage) / 10000), currency1.decimals),
        parseUnits(String(typedAmount2 * (10000 - allowedSlippage) / 10000), currency2.decimals),
      ]
      const txResult = await fetchTxResponse(() => stableSwapContract.write.remove_liquidity(
        [amountToRemove.quotient, [tokenAmounts[0], tokenAmounts[1], tokenAmounts[2]]], {
        // gas: DEFAULT_GAS_LIMIT,
        gasPrice,
        account: stableSwapContract.account,
        chain: stableSwapContract.chain,
      }), {
        type: 'remove-liquidity-stable',
        summary: `Remove ${typedAmount0.toFixed(1)} ${currency0?.symbol}, ${typedAmount1.toFixed(1)} ${currency1?.symbol}, ${typedAmount2.toFixed(1)} ${currency2?.symbol},`,
        translatableSummary: {
          text: `Remove %amount0% %symbol0%, %amount1% %symbol1%, %amount2% %symbol2%`,
          data: {
            amount0: typedAmount0.toFixed(1),
            symbol0: currency0?.symbol,
            amount1: typedAmount1.toFixed(1),
            symbol1: currency1?.symbol,
            amount2: typedAmount2.toFixed(1),
            symbol2: currency2?.symbol,
          },
        },
      }).catch((ex) => { console.error('add_liquidity error', ex) })

      if (txResult !== null) {
        // if (txResult) {
        //   hashRef.current = txResult.hash
        // }
        // setTxHash(txResult)
        onDismiss?.()
      }
    } catch (ex) {
      console.error("error", ex)
    }
    setAttemptingTxn(false)
  }, [allowedSlippage, amountToRemove, currency0, currency1, currency2, fetchTxResponse, gasPrice, onDismiss, percent, stableSwapContract, typedAmount0, typedAmount1, typedAmount2])

  const translationData = useMemo(
    () => ({
      amountA: typedAmount0.toFixed(1),
      symbolA: pool.token0.symbol,
      amountB: typedAmount1.toFixed(1),
      symbolB: pool.token1.symbol,
      amountC: typedAmount2.toFixed(1),
      symbolC: pool.token2.symbol,
    }),
    [pool, typedAmount0, typedAmount1, typedAmount2],
  )

  const pendingText = useMemo(
    () =>
      t('Removing %amountA% %symbolA%, %amountB% %symbolB%, %amountC% %symbolC%', translationData),
    [t, translationData],
  )

  const [onPresentConfirmModal] = useModal(
    <TransactionConfirmationModal
      minWidth={['100%', null, '420px']}
      title={t('Remove Liquidity')}
      // customOnDismiss={}
      attemptingTxn={attemptingTxn}
      hash={''}
      content={() => null}
      pendingText={pendingText}
    />,
    false,
    true,
    'TransactionConfirmationModal',
  )

  const handleButtonSubmit = () => {
    onPresentConfirmModal()
    onRemove()
  }

  return (
    <Modal title="Remove Liquidity" onDismiss={onDismiss}>
      <SliderContainer width={['100%', , '500px']}>
        <SliderRow mb={2}>
          <Box style={{ flex: 1 }}>
            <Slider
              name="lp-amount"
              min={0}
              max={100}
              value={percent}
              onValueChanged={(v: number) => setPercent(Math.ceil(v))}
            />
          </Box>
          <Text fontSize={32} minWidth="3em" textAlign="right">
            {percent}%
          </Text>
        </SliderRow>
        <ButtonGrid>
          <PercentageButton selected={percent === 25} onClick={() => setPercent(25)}>
            25%
          </PercentageButton>
          <PercentageButton selected={percent === 50} onClick={() => setPercent(50)}>
            50%
          </PercentageButton>
          <PercentageButton selected={percent === 75} onClick={() => setPercent(75)}>
            75%
          </PercentageButton>
          <PercentageButton selected={percent === 100} onClick={() => setPercent(100)}>
            {t('Max')}
          </PercentageButton>
        </ButtonGrid>
      </SliderContainer>
      <FlexGap flexDirection="column" gap="8px" mt={4}>
        <Text fontSize={15}>
          {t('Receive')}
        </Text>
        <FlexGap gap="8px">
          <CurrencyLogo currency={currency0} size="17px" />
          <Text color="textSubtle" fontSize={12}>
            {currency0?.symbol}
          </Text>
          <Balance value={typedAmount0} decimals={4} fontSize={12} ml="auto" />
        </FlexGap>
        <FlexGap gap="8px">
          <CurrencyLogo currency={currency1} size="17px" />
          <Text color="textSubtle" fontSize={12}>
            {currency1?.symbol}
          </Text>
          <Balance value={typedAmount1} decimals={4} fontSize={12} ml="auto" />
        </FlexGap>
        <FlexGap gap="8px">
          <CurrencyLogo currency={currency2} size="17px" />
          <Text color="textSubtle" fontSize={12}>
            {currency2?.symbol}
          </Text>
          <Balance value={typedAmount2} decimals={4} fontSize={12} ml="auto" />
        </FlexGap>
      </FlexGap>
      <Hr />
      <FlexGap gap="8px" justifyContent="space-between" alignItems="center">
        <Text color="textSubtle" fontSize={12}>
          {t('Slippage Tolerance')}
          <IconButton scale="sm" variant="text" onClick={onPresentSettingsModal}>
            <PencilIcon color="textSubtle" width="10px" />
          </IconButton>
        </Text>
        <Text fontSize={12}>
          {allowedSlippage / 100}%
        </Text>
      </FlexGap>
      <FlexGap gap="8px" justifyContent="space-between" alignItems="center">
        <TooltipText ref={targetRef} fontSize="12px" color="textSubtle">
          {t('LP reward APR')}
        </TooltipText>
        {tooltipVisible && tooltip}
        <Balance fontSize={12} value={pool.apr7d} unit="%" decimals={2} />
      </FlexGap>
      <Box mt="16px">
        {!account ? (
          <ConnectWalletButton width="100%" style={{ minHeight: '72px' }} />
        ) : (
          <FlexGap gap="8px">
            {
              (approvalState === ApprovalState.NOT_APPROVED || approvalState === ApprovalState.PENDING) &&
              <Button
                onClick={approveCallback}
                width="100%"
                style={{ minHeight: '72px' }}
                disabled={!percent || approvalState === ApprovalState.PENDING || approvingTxn}
              >
                {
                  (approvalState === ApprovalState.PENDING || approvingTxn)
                    ? <Dots>{t('Approving')}</Dots>
                    : t('Approve')
                }
              </Button>
            }
            <Button
              onClick={handleButtonSubmit}
              width="100%"
              style={{ minHeight: '72px' }}
              disabled={!percent || approvalState !== ApprovalState.APPROVED}
            >
              {t('Remove')}
            </Button>
          </FlexGap>
        )}
      </Box>
    </Modal>
  )
}

export default DecreaseModalStable